import type { components } from '@/generated/internal-api/openapi-types';
import type { StudyApiId } from '@/model/Study.model';
import { ApiService } from '@/support/ApiService';
import type { AltId, Animal, AnimalApiId } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import { _isBlank, _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash.ts';
import { validAnimalNameLength } from '@/components/Colony/Animals/Animals.utils.tsx';

export interface AnimalIdentifierData {
  id: ID;
  api_id?: AnimalApiId;
  name: string;
  cage?: Cage;
  alt_ids: Animal['alt_ids'];
}

export interface HoTColumn {
  title: string;
  data: string;
  readOnly?: boolean;
  validator?: (value: unknown, callback: (valid: boolean) => void) => void;
  validationErrorMessage?: (value: unknown) => string;
}

export function checkStudyForDuplicates(
  value: string,
  altIdType: AltId,
  studyApiId: StudyApiId,
  animalApiId: AnimalApiId
): Promise<boolean> {
  return ApiService.call({
    endpoint: 'POST /api/v1/studies/{studyId}/animals/{animalId}/{altIdType}/verify-unique',
    path: { studyId: studyApiId, animalId: animalApiId, altIdType },
    body: { alt_id_value: value },
    options: {
      // This runs as a Handsontable validator on every tag edit, so a transient
      // network blip must not spam a user-facing toast. Still capture to Sentry
      // (tagged with a feature-specific slug) for observability.
      onError: {
        toast: false,
        slug: 'assign-identifiers-tag-unique-check',
      },
    },
  }).then((result) => result?.body?.success === true);
}

/**
 * Request/response types for the bulk alt-id uniqueness endpoint
 * `POST /api/v1/animals/alt-ids/verify-unique-batch` (BNCH-148962), sourced from
 * the generated OpenAPI schema.
 */
export type VerifyUniqueBatchItem = components['schemas']['AltIdVerifyUniqueItemV1'];
export type VerifyUniqueBatchRequest = components['schemas']['AltIdVerifyUniqueBatchRequestV1'];
export type VerifyUniqueConflict = components['schemas']['AltIdVerifyUniqueConflictV1'];
export type VerifyUniqueBatchResult = components['schemas']['AltIdVerifyUniqueResultV1'];
export type VerifyUniqueBatchResponse = components['schemas']['AltIdVerifyUniqueBatchResponseV1'];

/**
 * Uniqueness scope for the batch endpoint. The finalized contract is study-only
 * (no workspace mode and no `type` field) with a required `study_id`.
 *
 * TODO(BNCH-148962): switch to the generated
 * `components['schemas']['AltIdVerifyUniqueScopeV1']` once the finalized
 * spec lands in the synced overwatch-server ref — the currently generated schema
 * is still the interim contract (optional `type` + optional `study_id`).
 */
export type VerifyUniqueScope = { study_id: StudyApiId };

interface VerifyUniqueBatchArgs {
  studyApiId?: StudyApiId;
  items: Array<VerifyUniqueBatchItem>;
  altIdType?: AltId;
}

/**
 * Verifies many alt-id values for uniqueness in a single request, replacing the
 * per-cell `checkStudyForDuplicates` round-trips. The finalized contract is
 * study-scoped only: the request carries `{ study_id }` and the endpoint can
 * only be called when a study api id is present. Without one (e.g. colony
 * animals not tied to a study) no request is made and every checkable item is
 * reported unique — in-table uniqueness is still enforced synchronously by the
 * Handsontable validator.
 *
 * Blank values are skipped client-side and reported as unique, so no request is
 * made when every item is blank. Results are keyed by the caller-supplied `ref`;
 * result order is not guaranteed.
 */
export async function verifyUniqueBatch({
  studyApiId,
  items,
  altIdType = 'tag',
}: VerifyUniqueBatchArgs): Promise<Array<VerifyUniqueBatchResult>> {
  const blankResults: Array<VerifyUniqueBatchResult> = [];
  const checkableItems: Array<VerifyUniqueBatchItem> = [];
  items.forEach((item) => {
    const value = _notNil(item.value) ? String(item.value) : '';
    if (_isBlank(value)) {
      blankResults.push({ ref: item.ref ?? '', value, unique: true, conflicts: [] });
    } else {
      checkableItems.push({ ref: item.ref, value, animal_id: item.animal_id });
    }
  });

  if (_isEmpty(checkableItems)) {
    return blankResults;
  }

  // The finalized contract has no workspace/colony scope, so the endpoint can
  // only be called with a study. Without a study api id, report every checkable
  // item as unique and skip the request; in-table uniqueness is already enforced
  // synchronously by the Handsontable validator.
  if (_isNil(studyApiId)) {
    const uniqueResults = checkableItems.map<VerifyUniqueBatchResult>((item) => ({
      ref: item.ref ?? '',
      value: item.value ?? '',
      unique: true,
      conflicts: [],
    }));
    return [...uniqueResults, ...blankResults];
  }

  const scope: VerifyUniqueScope = { study_id: studyApiId };
  const body: VerifyUniqueBatchRequest = { alt_id_type: altIdType, scope, items: checkableItems };

  const result = await ApiService.call({
    endpoint: 'POST /api/v1/animals/alt-ids/verify-unique-batch',
    body,
    options: {
      onError: {
        toast: false,
        slug: 'assign-identifiers-tag-unique-batch',
      },
    },
  });
  return [...(result?.body?.results ?? []), ...blankResults];
}

export const HoTValidators = {
  nameValidator: (value: unknown, callback: (check: boolean) => void) => {
    if (!value) {
      callback(false);
      return;
    }
    callback(!(typeof value === 'string' && !validAnimalNameLength(value)));
  },
};

export const HoTcolumns: Array<HoTColumn> = [
  {
    title: 'Cage',
    data: 'cage.name',
    readOnly: true,
  },
  {
    title: 'Name',
    data: 'name',
    validator: HoTValidators.nameValidator,
    validationErrorMessage: (value: unknown) => {
      if (!value) {
        return 'Required Field';
      }
      if (typeof value === 'string' && value.length > 40) {
        return 'Name must be 40 characters or less';
      }
      return `${value} is not valid`;
    },
  },
  {
    title: 'Tail',
    data: 'alt_ids.tail',
  },
  {
    title: 'Ear',
    data: 'alt_ids.ear',
  },
  {
    title: 'Tag',
    data: 'alt_ids.tag',
  },
  {
    title: 'Donor',
    data: 'alt_ids.donor',
  },
];

// Should be exported to the API as a SSOT
export const identifierTypes: Array<AltId> = ['tag', 'tail', 'ear', 'donor'];

export const filterEmptyAltIds = (animals: Array<Animal>) => {
  return animals.map((animal) => ({
    ...animal,
    alt_ids: _isNotEmpty(animal.alt_ids) ? animal.alt_ids : null,
  }));
};

const TAG_DUPLICATE_MESSAGE = 'This identifier is already in use';

interface BuildHoTcolumnsArgs {
  tagUnique: boolean;
  /**
   * Reports whether the given row currently holds a tag value that the batched
   * server-side uniqueness check flagged as an `existing` conflict. This lets the
   * (synchronous) validator surface server results without issuing its own
   * request — the single batch call lives in the modal (`verifyUniqueBatch`).
   */
  getServerConflict?: (row: number, value: string) => boolean;
}

/**
 * Handsontable validator for the Tag column. Runs fully synchronously: it
 * stringifies the value, treats blank as valid, rejects duplicates within the
 * loaded table, and rejects values the batched server check reported as already
 * in use. Used only when the `assign_identifiers_tag_unique` feature flag is on.
 *
 * `this.instance` is used instead of a closure-captured hot instance because the
 * instance is `null` at initial render, which would permanently break validation.
 */
const buildTagValidator = (getServerConflict?: (row: number, value: string) => boolean) =>
  function (
    this: { row: number; instance: { getSourceData: () => Array<AnimalIdentifierData> } },
    value: unknown,
    callback: (valid: boolean) => void
  ): void {
    // Stringify first: a non-string value (e.g. a numeric tag) would otherwise
    // collapse to `null`, read as blank, and skip uniqueness validation.
    const tagValue = _notNil(value) ? String(value) : '';
    if (_isBlank(tagValue)) {
      callback(true);
      return;
    }

    const sourceData = this.instance.getSourceData();
    const duplicateInTable = sourceData.some(
      (dataRow, index) => index !== this.row && dataRow?.alt_ids?.tag === tagValue
    );
    if (duplicateInTable) {
      callback(false);
      return;
    }

    // Server-wide `existing` conflicts are detected by a single batched request
    // in the modal and fed back here, so Handsontable stays the single source of
    // truth for cell validity (invalid highlight + Save gating).
    if (getServerConflict?.(this.row, tagValue) === true) {
      callback(false);
      return;
    }

    callback(true);
  };

/**
 * Builds the HoT columns for the Assign Identifiers modal. The Tag column is
 * always present; its uniqueness validator is attached only when the
 * `assign_identifiers_tag_unique` flag is enabled.
 */
export const buildHoTcolumns = ({ tagUnique, getServerConflict }: BuildHoTcolumnsArgs): Array<HoTColumn> =>
  HoTcolumns.map((column): HoTColumn => {
    if (column.data !== 'alt_ids.tag' || !tagUnique) {
      return column;
    }
    return {
      ...column,
      validator: buildTagValidator(getServerConflict),
      validationErrorMessage: () => TAG_DUPLICATE_MESSAGE,
    };
  });
